import leadStyles from '@/features/leads/Leads.module.css';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import { StateMessage } from '@/components/StateMessage/StateMessage';
import type { AcquisitionCatalogEntry } from '@marketing-hub/shared';
import { AcquisitionCatalogTiles } from './AcquisitionCatalogTiles';
import type { ScraperCapabilities, ScrapeJobFormState } from './scrapeJobForm';
import styles from '@/pages/ProjectsPage.module.css';

type Props = {
  form: ScrapeJobFormState;
  onChange: (patch: Partial<ScrapeJobFormState>) => void;
  scrapeCaps?: ScraperCapabilities;
  catalogEntries?: AcquisitionCatalogEntry[];
  hideSourceSelect?: boolean;
};

function SegmentationFields({
  form,
  onChange,
}: {
  form: ScrapeJobFormState;
  onChange: (patch: Partial<ScrapeJobFormState>) => void;
}) {
  return (
    <>
      <Field label="Niche (optioneel)">
        <TextInput
          value={form.apifyNiche}
          onChange={(e) => onChange({ apifyNiche: e.target.value })}
          placeholder="tandarts"
        />
      </Field>
      <Field label="Regio (optioneel)">
        <TextInput
          value={form.apifyRegion}
          onChange={(e) => onChange({ apifyRegion: e.target.value })}
          placeholder="Amsterdam"
        />
      </Field>
      <Field
        label={
          form.jobSource === 'google_serp'
            ? 'Keywords (één per regel, optioneel)'
            : 'Zoekqueries (één per regel, optioneel)'
        }
      >
        <TextArea
          value={form.apifyQueries}
          onChange={(e) => onChange({ apifyQueries: e.target.value })}
          rows={3}
          placeholder={
            form.jobSource === 'google_serp'
              ? 'marketing bureau rotterdam\nB2B software nederland'
              : 'marketing bureau rotterdam'
          }
        />
      </Field>
      <Field label="Max resultaten">
        <TextInput
          type="number"
          min={1}
          max={form.jobSource === 'google_serp' ? 300 : 500}
          value={String(form.apifyMaxItems)}
          onChange={(e) => onChange({ apifyMaxItems: Number(e.target.value) || 200 })}
        />
      </Field>
      <label className={styles.mutedSmall}>
        <input
          type="checkbox"
          checked={form.websiteEnrichEnabled}
          onChange={(e) => onChange({ websiteEnrichEnabled: e.target.checked })}
        />{' '}
        Website scannen voor e-mail (aanbevolen — Places/Apify leveren zelden direct e-mail)
      </label>
      <p className={styles.mutedSmall}>
        Segmentatie: minimaal één query, niche of regio. Resultaten gaan naar de review-queue.
      </p>
    </>
  );
}

export function ScrapeJobSourceFields({
  form,
  onChange,
  scrapeCaps,
  catalogEntries,
  hideSourceSelect,
}: Props) {
  const placesOk = Boolean(scrapeCaps?.google_places?.configured);
  const serpOk = Boolean(scrapeCaps?.google_serp?.configured);
  const apifyOk = Boolean(scrapeCaps?.apify.configured);

  return (
    <>
      {!hideSourceSelect ? (
        <Field label="Bron">
          <select
            className={leadStyles.filterInput}
            value={form.jobSource}
            onChange={(e) =>
              onChange({ jobSource: e.target.value as ScrapeJobFormState['jobSource'] })
            }
          >
            <option value="google_places" disabled={!placesOk}>
              Google Places API (aanbevolen){placesOk ? '' : ' — niet geconfigureerd'}
            </option>
            <option value="google_serp" disabled={!serpOk}>
              Google Search SERP (keywords){serpOk ? '' : ' — CSE niet geconfigureerd'}
            </option>
            <option value="apify" disabled={!apifyOk}>
              Apify (Maps / Search fallback){apifyOk ? '' : ' — geen token'}
            </option>
            <option value="urls">Seed URLs (HTTP)</option>
          </select>
        </Field>
      ) : null}

      {form.jobSource === 'urls' ? (
        <>
          <Field label="Fetch engine">
            <select
              className={leadStyles.filterInput}
              value={form.fetchEngine}
              onChange={(e) =>
                onChange({ fetchEngine: e.target.value as ScrapeJobFormState['fetchEngine'] })
              }
            >
              {(scrapeCaps?.fetch.engines ?? [
                { id: 'fetch' as const, label: 'Static HTML (fetch)' },
                { id: 'playwright' as const, label: 'Browser (Playwright)' },
              ]).map((e) => (
                <option
                  key={e.id}
                  value={e.id}
                  disabled={e.id === 'playwright' && !scrapeCaps?.fetch.playwright_enabled}
                >
                  {e.label}
                  {e.id === 'playwright' && !scrapeCaps?.fetch.playwright_enabled
                    ? ' — niet op server'
                    : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Seed URLs (één per regel)">
            <TextArea
              value={form.seedUrls}
              onChange={(e) => onChange({ seedUrls: e.target.value })}
              rows={4}
            />
          </Field>
        </>
      ) : null}

      {form.jobSource === 'google_places' ? (
        <>
          {!placesOk ? (
            <StateMessage title="Google Places API">
              Zet <code>GOOGLE_PLACES_API_KEY</code> in server-.env (API + worker), schakel{' '}
              <strong>Places API (New)</strong> in Google Cloud in, en herstart containers.
            </StateMessage>
          ) : (
            <p className={styles.mutedSmall}>
              Officiële Google Places Text Search — stabielere bedrijfsnamen, adressen en websites
              dan Apify-scrapers. E-mail via website-enrich.
            </p>
          )}
          {catalogEntries?.length ? (
            <AcquisitionCatalogTiles
              entries={catalogEntries}
              onApply={(patch) =>
                onChange({
                  ...patch,
                  jobSource: placesOk ? 'google_places' : 'apify',
                })
              }
            />
          ) : null}
          <SegmentationFields form={form} onChange={onChange} />
        </>
      ) : null}

      {form.jobSource === 'google_serp' ? (
        <>
          {!serpOk ? (
            <StateMessage title="Google Search (SERP)">
              Maak een{' '}
              <a
                href="https://programmablesearchengine.google.com/"
                target="_blank"
                rel="noreferrer"
              >
                Programmable Search Engine
              </a>{' '}
              (hele web), schakel <strong>Custom Search API</strong> in Cloud Console in, zet{' '}
              <code>GOOGLE_CSE_CX</code> en <code>GOOGLE_CSE_API_KEY</code> (of hergebruik Places-key)
              in server-.env, herstart API + worker. Gratis tier ~100 queries/dag.
            </StateMessage>
          ) : (
            <p className={styles.mutedSmall}>
              Officiële Custom Search JSON API — robuuste keyword-SERP met NL-bias (gl/lr). Unieke
              domeinen per run; e-mail via website-enrich.
            </p>
          )}
          {catalogEntries?.length ? (
            <AcquisitionCatalogTiles
              entries={catalogEntries}
              onApply={(patch) =>
                onChange({
                  ...patch,
                  jobSource: serpOk ? 'google_serp' : placesOk ? 'google_places' : 'apify',
                })
              }
            />
          ) : null}
          <SegmentationFields form={form} onChange={onChange} />
        </>
      ) : null}

      {form.jobSource === 'apify' ? (
        <>
          {catalogEntries?.length ? (
            <AcquisitionCatalogTiles entries={catalogEntries} onApply={(patch) => onChange(patch)} />
          ) : null}
          {!apifyOk ? (
            <StateMessage title="Apify">
              Zet <code>APIFY_API_TOKEN</code> in server-.env (worker + API) en herstart containers.
            </StateMessage>
          ) : (
            <p className={styles.mutedSmall}>
              Fallback via Apify Store actors — kwaliteit varieert; gebruik Google Places API als
              primaire bron.
            </p>
          )}
          <Field label="Apify preset">
            <select
              className={leadStyles.filterInput}
              value={form.apifyPreset}
              onChange={(e) =>
                onChange({ apifyPreset: e.target.value as ScrapeJobFormState['apifyPreset'] })
              }
            >
              {(scrapeCaps?.apify.presets ?? [
                { id: 'google_maps' as const, label: 'Google Maps' },
                { id: 'google_search' as const, label: 'Google Search' },
              ]).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
          <SegmentationFields form={form} onChange={onChange} />
        </>
      ) : null}
    </>
  );
}
